// src/lib/use-beds24-multipliers.js — Phase 6.B PR5B (ADR-097)
//
// Hook React qui charge en read-only les multipliers Beds24 (priceRules 2-16
// configurés en Linked Listings) d'un logement.
//
// Convention codebase : src/lib/use-*.js (cf. use-daily-pricing.js +
// use-pricing-config-list.js — pattern ADR-076).
//
// Comportement obligatoire :
//   - Shortcircuit logementId nul/vide : aucun fetch déclenché
//   - AbortController (cohérent useDailyPricing) : annule fetch précédent si
//     logementId change
//   - Pas de cache useRef (TTL 0 — décision Dimitri D-4 plan PR5 v1.2)
//   - refetch() exposé via useCallback stable (identité référentielle stable)
//
// Cf. ADR-097 (multipliers source vérité Beds24) + plan PR5 v1.2 § 5.

import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "../supabase";

/**
 * Charge les multipliers Beds24 configurés (priceRules 2-16) pour un logement.
 *
 * @param {string|null|undefined} logementId — id format pricing (ex: 'p1', 'p6.lc1')
 * @returns {{
 *   multipliers: Array<{
 *     priceRule: number,
 *     name: string,
 *     offsetMultiplier: number,
 *     offsetAmount: number,
 *     priceId: number|null,
 *     channels: Record<string, { enable: boolean, rateCode?: string }>,
 *   }>,
 *   channelMinStays: Array<{ priceRule: number, name: string, minimumStay: number, channelLabel: string }>,
 *   loading: boolean,
 *   error: string|null,
 *   refetch: () => void,
 * }}
 *
 * Note WAGON R : `channelMinStays` provient de la MÊME réponse endpoint
 * (champ additif read-only, A′ Dimitri 13/06) — chaque instance du hook fait SON GET (deux composants = deux GET idempotents read-only — un lift en props serait l'optimisation future).
 * Les consommateurs qui ne lisent que `multipliers` ignorent le champ.
 */
export function useBeds24Multipliers(logementId) {
  const [state, setState] = useState({
    multipliers: [],
    channelMinStays: [],
    loading: false,
    error: null,
  });

  // Compteur interne pour forcer re-fetch via refetch() (cohérent pattern
  // BPR3-3 useDailyPricing.invalidate()).
  const [refetchTick, setRefetchTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Shortcircuit si pas d'id (cohérent useDailyPricing BPRv3-3).
    if (!logementId) {
      setState({
        multipliers: [],
        channelMinStays: [],
        loading: false,
        error: null,
      });
      return undefined;
    }

    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: null }));

    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (controller.signal.aborted) return;
        if (!session?.access_token) {
          if (!mountedRef.current) return;
          setState({
            multipliers: [],
            channelMinStays: [],
            loading: false,
            error: "not_authenticated",
          });
          return;
        }

        const qs = new URLSearchParams({
          action: "beds24_multipliers_read",
          logement_id: logementId,
        });
        const res = await fetch(`/api/beds24-mapping?${qs.toString()}`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (controller.signal.aborted) return;

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (controller.signal.aborted) return;
          if (!mountedRef.current) return;
          setState({
            multipliers: [],
            channelMinStays: [],
            loading: false,
            error: body.error || `http_${res.status}`,
          });
          return;
        }

        const json = await res.json();
        if (controller.signal.aborted) return;
        if (!mountedRef.current) return;

        setState({
          multipliers: Array.isArray(json.multipliers) ? json.multipliers : [],
          channelMinStays: Array.isArray(json.channelMinStays)
            ? json.channelMinStays
            : [],
          loading: false,
          error: null,
        });
      } catch (e) {
        if (e.name === "AbortError") return;
        if (controller.signal.aborted) return;
        if (!mountedRef.current) return;
        setState({
          multipliers: [],
          channelMinStays: [],
          loading: false,
          error: e.message || String(e),
        });
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logementId, refetchTick]);

  const refetch = useCallback(() => {
    setRefetchTick((t) => t + 1);
  }, []);

  return {
    multipliers: state.multipliers,
    channelMinStays: state.channelMinStays,
    loading: state.loading,
    error: state.error,
    refetch,
  };
}
